<template lang="html">
  <v-sheet id="coloring-options" color="transparent">
    <div class="d-flex flex-row align-center">
      <v-sheet color="white" width="220px" rounded>
        <v-select
          color="primary"
          v-model="selectedOption"
          :items="options"
          item-text="name"
          item-value="id"
          dense
          hide-details
          filled
          :loading="loading"
          @change="onOptionChange"></v-select>
      </v-sheet>
    </div>

    <div class="my-1 mx-1" v-if="coloringSchema.length">
      <v-row
        v-for="(color, index) of coloringSchema"
        :key="color.name + index"
        cols="12"
        class="mt-0">
        <v-col cols="auto">
          <v-icon :color="color.color" @click="zoomToFitColor(color.color)">mdi-circle</v-icon>
        </v-col>
        <v-col class="px-0">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span style="color:#f2f2f2" v-bind="attrs" v-on="on">{{ $truncateString(color.name, 32) }}</span>
            </template>
            <span>{{ color.name }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
  </v-sheet>
</template>

<script>
  import { TaskFieldType } from '@/features/Share/config/enums';
  import { $truncateString } from '@/utils';
  import { zoomToFit } from '@/utils/forgeUtils';
  import { getTaskEffectiveTagIds } from '@Task/utils/taskIsolation';
  import { useSpatialFocus } from '@/features/Dashboard/composables/useSpatialFocus';
  import { mapActions, mapGetters } from 'vuex';
  import { error } from '@/utils/debugLogger';

  /**
   * ONE spatial authority (Plan C). This component chooses WHICH tasks and WHICH property colours
   * them — intent only. It never resolves dbIds, ghost contexts, target precedence, pulse sets,
   * a fit set or a generation; `useSpatialFocus` owns all of that behind one entity scope.
   *
   * The composable is a thin façade over a module-scoped `WeakMap<viewer, state>`, so taking it at
   * module load (rather than per instance) is deliberate: two `ViewerTaskColoring` hosts — the
   * Task board and `pageView.vue` — share one per-viewer generation instead of fighting.
   */
  const focus = useSpatialFocus();

  const FOCUS_OWNER_ID = 'task-viewer-coloring';
  const DEFAULT_BOARD_TITLE = 'Task Board';
  const CRITICAL_LABEL = 'Critical';
  const NOT_CRITICAL_LABEL = 'Not Critical';
  const CRITICAL_COLOR = '#FF0000';
  const NOT_CRITICAL_COLOR = '#AAAAAA';

  /** Turn a value -> hex lookup into the `encoding` contract the composable consumes. */
  function encodingFrom({ column, displayName, valueOf, colors }) {
    return {
      column,
      displayName,
      valueOf,
      colorFor: (value) => (value === null || value === undefined ? null : colors.get(value) || null),
    };
  }

  export default {
    name: 'ViewerTaskColoring',
    props: {
      isolationInfoByTaskId: {
        type: Object,
        default: () => ({}),
      },
      isolatedTags: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        loading: false,
        statusColoringSchema: [],
        onOptionChangeTimeout: null,
      };
    },
    methods: {
      $truncateString,
      ...mapActions({
        getTagsInfo: 'GET_BASE_TAGS_INFO_BY_IDS',
      }),
      onOptionChange(timeout = 200) {
        if (this.onOptionChangeTimeout) {
          clearTimeout(this.onOptionChangeTimeout);
        }
        this.onOptionChangeTimeout = setTimeout(() => {
          this.onOptionChangeTimeout = null;
          this.executeOptionChange();
        }, timeout);
      },
      /**
       * Debounce-free, awaitable entry point. The Task view sequences its retained crop/scaffold
       * camera helpers AFTER the shared scene renders, so it needs to know when that has happened.
       */
      async applyColoringNow() {
        if (this.onOptionChangeTimeout) {
          clearTimeout(this.onOptionChangeTimeout);
          this.onOptionChangeTimeout = null;
        }
        await this.executeOptionChange();
      },
      async executeOptionChange() {
        await this.applyTaskFocus();
      },
      /**
       * Resolve the colour ENCODING for the current option: which property supplies the legend
       * values and which hex each value renders as. `null` means a plain, unencoded isolate.
       *
       * The composable is handed the VALUE (`encodingValue`) and a `colorFor` resolver, never a
       * per-task hex — the legend's rows are property values, and the arbitrator needs the value
       * identity to detect a MIXED conflict on one address.
       */
      resolveEncoding() {
        const option = this.selectedOption;
        if (!option || option === 'default') return null;
        if (this.availablePlan3dFields.some((f) => f.id === option)) {
          return this.customFieldEncoding(option);
        }
        switch (option) {
          case 'status': return this.statusEncoding();
          case 'dates': return this.datesEncoding();
          case 'criticalPath': return this.criticalPathEncoding();
          case 'task': return this.taskEncoding();
          default: return null;
        }
      },
      statusEncoding() {
        const byId = new Map();
        const colors = new Map();
        (this.statusColoringSchema || []).forEach((status) => {
          if (!status) return;
          byId.set(status.id, status.name);
          if (status.name) colors.set(status.name, status.color || null);
        });
        return encodingFrom({
          column: 'statusId',
          displayName: 'Status',
          valueOf: (task) => byId.get(task?.statusId) ?? null,
          colors,
        });
      },
      datesEncoding() {
        // The schedule status travels ON the task, so the palette is read from the selection
        // itself rather than from a separate schema that may not be loaded yet.
        const colors = new Map();
        this.selectedTaskObjects.forEach((task) => {
          const status = task?.currentTaskStatus;
          if (status?.name) colors.set(status.name, status.color || null);
        });
        return encodingFrom({
          column: 'currentTaskStatus',
          displayName: 'Scheduled',
          valueOf: (task) => task?.currentTaskStatus?.name ?? null,
          colors,
        });
      },
      criticalPathEncoding() {
        const gantt = window.gantt;
        return encodingFrom({
          column: 'criticalPath',
          displayName: 'Critical Path',
          valueOf: (task) => (gantt?.isCriticalTask?.(task) ? CRITICAL_LABEL : NOT_CRITICAL_LABEL),
          colors: new Map([
            [CRITICAL_LABEL, CRITICAL_COLOR],
            [NOT_CRITICAL_LABEL, NOT_CRITICAL_COLOR],
          ]),
        });
      },
      taskEncoding() {
        const colors = new Map();
        this.selectedTaskObjects.forEach((task) => {
          if (task?.text) colors.set(task.text, task.color || null);
        });
        return encodingFrom({
          column: 'task',
          displayName: 'Task',
          valueOf: (task) => task?.text ?? null,
          colors,
        });
      },
      customFieldEncoding(fieldId) {
        const field = this.taskTypeFields.find((f) => f.id === fieldId);
        if (!field) return null;
        const options = field.options || [];
        const colors = new Map(options
          .filter((option) => !!option?.value)
          .map((option) => [option.value, option.color || null]));
        return encodingFrom({
          column: fieldId,
          displayName: field.label || fieldId,
          valueOf: (task) => {
            const customField = (task?.customFields || []).find((cf) => cf.fieldId === fieldId);
            const option = customField?.value
              ? options.find((o) => o.id === customField.value)
              : null;
            return option?.value ?? null;
          },
          colors,
        });
      },
      /**
       * Hand the current selection to the one composable as an entity scope.
       *
       * Every isolated task is the same stable subject kind — a direct-tag task, a CML-target task
       * and an anomaly-target task are all `{kind:'task', id}`. The server resolves which context
       * greys and which point of interest colours; this component never does.
       */
      async applyTaskFocus() {
        if (!this.viewer) return;

        const selectedTasks = this.selectedTaskObjects;
        if (!selectedTasks.length) {
          this.clearCurrentColoring();
          return;
        }

        const encoding = this.resolveEncoding();
        this.loading = true;
        try {
          const result = await focus.show({
            owner: this.focusOwner,
            scope: {
              kind: 'entities',
              // Order and repeats are preserved deliberately: the server contract treats the
              // entity list as the caller's exact row scope, not as a set.
              entities: selectedTasks.map((task) => (encoding
                ? { kind: 'task', id: task.id, encodingValue: encoding.valueOf(task) }
                : { kind: 'task', id: task.id })),
            },
            encoding: encoding
              ? {
                column: encoding.column,
                displayName: encoding.displayName,
                colorFor: encoding.colorFor,
              }
              : null,
            viewer: this.viewer,
            // Swap straight to the new selection: no whole-model flash, ghosting kept, and a
            // selection with nothing to show leaves the current colouring as it was.
            swapInPlace: true,
          });

          // The composable's generation is the ONLY concurrency authority (invariant 9). A
          // superseded/aborted gesture must not go on to paint scaffolds a newer gesture has
          // already replaced, and one with nothing to show kept the previous scene.
          if (result?.status !== 'rendered') {
            this.notifyNothingToShow(result);
            return;
          }

          // Scaffolds carry no dbIds, so they have no rendering address the composable can own.
          // Their colour stays on the scaffold-box extension — a retained low-level channel.
          const tags = await this.loadEffectiveTags(selectedTasks);
          this.colorDirectScaffolds(tags, selectedTasks, encoding);
        } catch (err) {
          error('Error applying task spatial focus:', err);
        } finally {
          this.loading = false;
        }
      },
      /** The previous colouring stays on screen, so say why the new selection did not replace it. */
      notifyNothingToShow(result) {
        const message = {
          empty: 'The selected tasks have no 3D location.',
          unrenderable: 'The selected tasks have no 3D location.',
          failed: 'Could not load the 3D view for the selected tasks.',
        }[result?.status];
        if (message) this.$dialog?.notify?.warning(message, { position: 'top-right', timeout: 3000 });
      },
      async loadEffectiveTags(selectedTasks) {
        const tagIds = [...new Set(selectedTasks.flatMap((task) => getTaskEffectiveTagIds(
          task,
          this.isolationInfoByTaskId[task.id],
        )))];
        const requestedIds = new Set(tagIds.map((id) => String(id).toLowerCase()));
        const alreadyLoaded = (this.isolatedTags || []).filter(
          (tag) => requestedIds.has(String(tag?.id).toLowerCase()),
        );
        const loadedIds = new Set(alreadyLoaded.map((tag) => String(tag?.id).toLowerCase()));
        if (tagIds.every((id) => loadedIds.has(String(id).toLowerCase()))) return alreadyLoaded;
        return this.getTagsInfo(tagIds);
      },
      colorDirectScaffolds(tags, selectedTasks, encoding) {
        (tags || [])
          .filter((tag) => tag.discriminator === 'ForgeScaffold')
          .forEach((tag) => {
            const normalizedTagId = String(tag.id).toLowerCase();
            const owner = [...selectedTasks].reverse().find((task) => {
              const isolationInfo = this.isolationInfoByTaskId[task.id];
              const ownsTag = getTaskEffectiveTagIds(task, isolationInfo)
                .some((id) => String(id).toLowerCase() === normalizedTagId);
              const isNarrow = (isolationInfo?.targets || []).some(
                (target) => String(target?.contextTagId).toLowerCase() === normalizedTagId,
              );
              return ownsTag && !isNarrow;
            });
            const color = owner && encoding ? encoding.colorFor(encoding.valueOf(owner)) : null;
            if (color) this.forgeService?.getScaffoldBoxExtension?.().setCustomColor(tag.id, color);
          });
      },
      clearCurrentColoring() {
        this.loading = false;
        // Dropping the owned scene also stops its pulse and un-themes it: one authority, one
        // teardown. A clear naming a different owner is refused by the composable itself.
        if (this.viewer) {
          focus.clear({ owner: this.focusOwner, viewer: this.viewer, reason: 'taskColoringCleared' });
          this.forgeService?.getScaffoldBoxExtension()?.resetColoring();
        }
      },
      resetColoring() {
        this.clearCurrentColoring();
      },
      clearColoring() {
        this.selectedOption = 'default';
        this.resetColoring();
      },
      getStatuses() {
        this.$store.dispatch('TASKSTATUS/GET_DATES_STATUS');
        this.$store
          .dispatch('TASK_MANAGER/GET_BY_ID', this.taskTypeId)
          .then((res) => {
            this.statusColoringSchema = res.statuses;
          })
          .finally(() => {
            this.loadType = false;
          });
      },
      /**
       * Choose the encoding only. Rendering is the caller's next step (`applyColoringNow`), so
       * this must not schedule a scene of its own — two scenes for one gesture would abort the
       * first request and flash the viewer.
       */
      checkCriticalPath() {
        if (this.isGanttActive && this.isCriticalPathActive) {
          this.selectedOption = 'criticalPath';
        }
      },
      backToKanban() {
        var path = `/${this.$route.params.id}/${this.$route.query.taskTypeId}/kanban`
        this.$router.push(path);
      },
      endMode() {
        this.isolatedTasks = [];
        this.clearColoring();
        this.forgeService?.getMainExtension()?.endIsolation();
      },
      /**
       * Legend-swatch camera affordance. A fit is not a context/target decision — it frames what
       * the shared scene already painted — so this stays a local low-level helper.
       */
      async zoomToFitColor(color) {
        const encoding = this.resolveEncoding();
        if (!encoding) return;

        const tagsIds = this.selectedTaskObjects
          .filter((task) => encoding.colorFor(encoding.valueOf(task)) === color)
          .flatMap((task) => getTaskEffectiveTagIds(
            task,
            this.isolationInfoByTaskId[task.id],
          ));

        if (!tagsIds.length) return;

        const tags = await this.getTagsInfo(tagsIds);

        let allDbIds = [];

        tags.forEach((tag) => {
          const dbIds = tag.children?.map((t) => t.dbId)?? [];
          allDbIds.push(...dbIds);
        });

        zoomToFit(this.viewer, allDbIds);
      },
      getCustomFieldsColoringSchema(fieldId) {
        const field = this.taskTypeFields.find((f) => f.id === fieldId);
        if (!field) return [];

        const options = field.options || [];
        const colors = options.map((option) => {
          return {
            name: option.value,
            color: option.color,
          };
        });

        return colors;
      },
    },
    computed: {
      ...mapGetters('TASK', {
        tasks: 'GET_TASKS',
      }),
      ...mapGetters('TASKSTATUS', {
        datesColoringSchema: 'GET_DATES_STATUS',
      }),
      selectedOption: {
        get() {
          return this.$store.getters['GET_TASK_VIEWER_COLORING_OPTION'];
        },
        set(value) {
          this.$store.commit('SET_TASK_VIEWER_COLORING_OPTION', value);
        },
      },
      viewer: {
        get() {
          return this.$store.state.viewer;
        },
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      taskTypeId: {
        get() {
          return this.$store.getters['VIEWERS_MODE/OPTIONS']?.metadata?.taskTypeId || this.$route.params?.taskTypeId;
        }
      },
      currentTaskType: {
        get() {
          return this.$store.getters['TASK_MANAGER/GET_CURRENT_TASK_TYPE']
        }
      },
      /** The widget that owns the scene, and the title the legend renders as its heading. */
      focusOwner: {
        get() {
          return {
            widgetId: FOCUS_OWNER_ID,
            title: this.currentTaskType?.name || DEFAULT_BOARD_TITLE,
          };
        }
      },
      taskTypeFields: {
        get() {
          return this.currentTaskType?.sections?.map((s) => s.fields).flat() || [];
        }
      },
      /** The isolated tasks, in selection order, that actually exist on the board. */
      selectedTaskObjects: {
        get() {
          return this.isolatedTasksObjects.filter((task) => !!task?.id);
        }
      },
      availablePlan3dFields: {
        get() {
          let fields = this.taskTypeFields.filter((f) => f.fieldType === TaskFieldType.Plan3D) || [];

          fields = fields.filter((f) => {
            return this.isolatedTasksObjects.some((t) => t?.customFields?.some((cf) => cf.fieldId === f.id && cf.value));
          });

          return fields.map((f) => {
            return {
              id: f.id,
              name: f.label,
            };
          });
        }
      },
      isolatedTasks: {
        get() {
          return this.$store.getters['TASK/GET_ISOLATED_TASKS'];
        },
        set(value) {
          this.$store.commit('TASK/SET_ISOLATED_TASKS', value);
        }
      },
      isolatedTasksObjects: {
        get() {
          return this.isolatedTasks.map((id) => this.tasks.find((t) => t.id === id));
        }
      },
      customSchema: {
        get() {
          let colors = this.isolatedTasksObjects.filter(t=>!!t).map((t) => {
            return {
              name: t.text,
              color: t.color,
            };
          }).filter((t) => t.color);

          // Group by color
          colors = colors.reduce((acc, curr) => {
            const existing = acc.find((item) => item.color === curr.color);
            if (existing) {
              existing.name += `, ${curr.name}`;
            } else {
              acc.push(curr);
            }
            return acc;
          }, []);

          return colors;
        }
      },
      coloringSchema() {
        switch (this.selectedOption) {
          case 'dates':
            return this.datesColoringSchema;
          case 'status':
            return this.statusColoringSchema;
          case 'criticalPath':
            return this.criticalPathSchema;
          case 'task':
            return this.customSchema;
          default:
            return this.getCustomFieldsColoringSchema(this.selectedOption);
        }
      },
      criticalPathSchema() {
        return [
          {
            name: 'Not Critical',
            color: '#AAAAAA',
          },
          {
            name: 'Critical',
            color: '#FF0000',
          },
        ];
      },
      options: {
        get() {
          let ops = [
            {
              id: 'default',
              name: 'Default',
            },
            {
              id: 'dates',
              name: 'Scheduled',
            },
            {
              id: 'status',
              name: 'Status',
            },
          ];

          if (this.isGanttActive && this.isCriticalPathActive) {
            ops.push({
              id: 'criticalPath',
              name: 'Critical Path',
            });
          }

          ops.push({ id: 'task', name: 'Task' })

          ops = [...ops, ...this.availablePlan3dFields];

          return ops;
        }
      },
      isGanttActive() {
        return this.$route.name === 'gantt';
      },
      isCriticalPathActive() {
        return this.$store.getters['GANTT/GET_HIGHLIGHT_CRITICAL_PATH'];
      },
    },
    mounted() {
      this.getStatuses();
    },
    beforeDestroy() {
      // Deliberately does NOT clear the scene. This component is `v-if`'d by the active viewer tab
      // (`Task/views/index.vue:270`) and by viewer mode (`pageView.vue:67`), so a tab switch
      // destroys it — and `focus.clear` would de-isolate the 3D viewer behind the user's back.
      // Nothing leaks: `applySpatialRenderPlan` drops the previous scene as its first step, the
      // adapter ledger lives in a per-viewer WeakMap that outlives this instance, and the two
      // explicit teardowns (`endMode`, `clearColoring`) still clear when the user means it.
      if (this.onOptionChangeTimeout) clearTimeout(this.onOptionChangeTimeout);
      this.onOptionChangeTimeout = null;
    },
    watch: {
      taskTypeId() {
        this.getStatuses();
      },
      // Defensive watcher for cold-load timing: on share-intent deep links
      // the component mounts (and the isolatedTasks watcher fires) BEFORE the
      // Forge viewer finishes loading. executeOptionChange returns early at
      // !this.viewer, and the watcher doesn't re-fire because isolatedTasks
      // didn't change. This watcher catches the moment the viewer becomes
      // available and re-triggers coloring.
      viewer: {
        handler(newViewer) {
          if (newViewer && this.isolatedTasks.length) {
            this.onOptionChange();
          }
        },
      },
      isolatedTasks: {
        handler(val) {
          if (!val.length) {
            this.clearColoring();
          } else {
            this.onOptionChange();
          }
        },
        immediate: true,
      },
      isolationInfoByTaskId: {
        handler() {
          if (this.viewer && this.isolatedTasks.length) this.onOptionChange(0);
        },
        deep: true,
      },
      isGanttActive: {
        handler(val) {
          if (!val && this.selectedOption === 'criticalPath') {
            this.clearColoring();
          }
        },
        immediate: true,
      },
      isCriticalPathActive: {
        handler(val) {
          if (val) {
            this.selectedOption = 'criticalPath';
            this.onOptionChange();
          } else if (this.selectedOption === 'criticalPath') {
            this.clearColoring();
          }
        },
        immediate: true,
      },
    },
  };
</script>
<style lang="scss">
</style>
